import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDesign } from './DesignContext';
import { isLabEnabled } from '../config/lab';
import { CONDITION_LABELS } from './ambient/weather';
import { TOD_WINDOWS } from './ambient/palettes';

const ALT_KEY_HINT = /Mac|iPhone|iPad/.test(
  typeof navigator !== 'undefined' ? navigator.platform : ''
)
  ? '⌥'
  : 'Alt+';

const TABS = [
  { id: 'appearance', label: 'Appearance' },
  ...(isLabEnabled() ? [{ id: 'lab', label: 'Lab' }] : []),
];

const MODES = [
  { id: 'system', label: 'System', key: '3' },
  { id: 'light', label: 'Light', key: '1' },
  { id: 'dark', label: 'Dark', key: '2' },
];

function AppearanceTab() {
  const {
    modePref,
    setModePref,
    ambient,
    setAmbient,
    todWindow,
    todLabel,
    condition,
    weather,
    resolvedMode,
  } = useDesign();

  const todName =
    todLabel || TOD_WINDOWS.find((w) => w.id === todWindow)?.label || todWindow;

  const wxParts = [
    weather.tempC != null && `${Math.round(weather.tempC)}°C`,
    CONDITION_LABELS[condition] || condition,
  ].filter(Boolean);

  return (
    <div>
      <div className="ada-appearance__readout">
        <strong>
          {todName} · {resolvedMode === 'dark' ? 'Dark' : 'Light'} mode
        </strong>
        <span>
          {ambient
            ? `Ambient sky ${wxParts.length ? `· ${wxParts.join(' · ')}` : ''}`
            : 'Ambient off — static midday palette'}
        </span>
      </div>

      <div className="ada-mode-seg" role="radiogroup" aria-label="Theme mode">
        {MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            role="radio"
            aria-checked={modePref === m.id}
            className={modePref === m.id ? 'is-active' : ''}
            onClick={() => setModePref(m.id)}
            title={`${m.label} (${ALT_KEY_HINT}${m.key})`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="ada-appearance__row">
        <span>Ambient time &amp; weather</span>
        <button
          type="button"
          className={ambient ? 'is-on' : ''}
          onClick={() => setAmbient(!ambient)}
          title={`Toggle (${ALT_KEY_HINT}0)`}
        >
          {ambient ? 'On' : 'Off'}
        </button>
      </div>
    </div>
  );
}

export default function DesignPicker() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('appearance');

  const subtitle =
    tab === 'appearance'
      ? 'Theme & ambient sky'
      : 'Stream demo';

  if (!open) {
    return (
      <button
        type="button"
        className="ada-picker-chip"
        onClick={() => setOpen(true)}
        aria-expanded="false"
        aria-controls="ada-design-picker"
        title="Show design options"
      >
        Appearance
      </button>
    );
  }

  return (
    <div className="ada-picker" id="ada-design-picker">
      <div className="ada-picker__header">
        <div className="ada-picker__title">
          <strong>Design options</strong>
          <span>{subtitle}</span>
        </div>
        <button
          type="button"
          className="ada-picker__close"
          onClick={() => setOpen(false)}
          aria-label="Close design options"
        >
          ×
        </button>
      </div>
      <div className="ada-picker__body">
        <div className="ada-picker__tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`ada-picker__tab${tab === t.id ? ' is-active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'appearance' && <AppearanceTab />}

        {tab === 'lab' && (
          <div className="ada-picker__lab">
            <p className="ada-picker__note">
              Demos of streaming + surfaces, outside the real chat routes.
            </p>
            <Link className="ada-picker__lab-link" to="/lab/stream-demo">
              Stream demo
            </Link>
            <Link className="ada-picker__lab-link" to="/lab/surface-demo">
              Surface demo
            </Link>
            <Link className="ada-picker__lab-link" to="/lab/model-picker">
              Model picker
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
